// src/features/ads/AddAdPage.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import type { AppDispatch, RootState } from "../../store/store";
import { createAd, fetchAdById, updateAd } from "../../features/ads/adsThunks";
import { fetchCurrentUser } from "../../features/auth/authThunks";
import Button from "@mui/material/Button";
import { resetAdsState } from "../../features/ads/adsSlice";
import './AddAds.css';
import PhoneInput from "../../components/PhoneInputComponent/PhoneInput";
import { Ad, Currenci, Currency } from "../../features/ads/adsTypes";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { getLocalizedText } from "../../features/ads/i18nFields";
import { toast } from "react-toastify";
import loaderStyles from "../../components/NavigationLoader/NavigationLoader.module.css";
import SimpleCaptcha, { createCaptchaChallenge, type CaptchaChallenge } from "../../components/SimpleCaptcha/SimpleCaptcha";
import { hasLeadingZeroPhoneDigit } from "../../utils/phoneValidation";
import { getCaptchaChallengeApi } from "../../api/captchaApi";
import AddressAutocompleteField from "../../components/AddressAutocompleteField/AddressAutocompleteField";
import { geocodeAddressApi, listCommunesByWilayaApi, listWilayasApi, reverseGeocodeCoordinatesApi, type AddressSuggestion, type CommuneOption, type GeocodeAddressResult, type WilayaOption } from "../../api/addressApi";
import { addAddressMemoryItem } from "../../features/addressMemory/addressMemorySlice";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

const TYPE_OPTIONS = [
  { key: "appartement", fr: "Appartement", en: "Apartment", ar: "شقة" },
  { key: "villa", fr: "Villa", en: "Villa", ar: "فيلا" },
  { key: "maison", fr: "Maison", en: "House", ar: "منزل" },
  { key: "studio", fr: "Studio", en: "Studio", ar: "استوديو" },
  { key: "hotel", fr: "Hotel", en: "Hotel", ar: "فندق" },
] as const;
const TITLE_MIN_LENGTH = 8;
const TITLE_MAX_LENGTH = 50;
const DESCRIPTION_MIN_LENGTH = 30;
const DESCRIPTION_MAX_LENGTH = 500;
const DESCRIPTION_LIST_MARKER_SOURCE = String.raw`[\p{Emoji_Presentation}\p{Extended_Pictographic}]\ufe0f?(?:\p{Emoji_Modifier})?(?:\u200d[\p{Emoji_Presentation}\p{Extended_Pictographic}]\ufe0f?(?:\p{Emoji_Modifier})?)*`;
const DESCRIPTION_LEADING_MARKERS_REGEX = new RegExp(`^\\s*(?:${DESCRIPTION_LIST_MARKER_SOURCE}\\s*)+`, "u");
const DESCRIPTION_MARKERS_ONLY_REGEX = new RegExp(`^\\s*(?:${DESCRIPTION_LIST_MARKER_SOURCE}\\s*)+$`, "u");
const hasDescriptionText = (value: string): boolean => /[\p{L}\p{N}]/u.test(value);
const hasManualIconListDescription = (value: string): boolean => (
  String(value || "")
    .split(/\r?\n/)
    .some((line, index, lines) => {
      if (DESCRIPTION_MARKERS_ONLY_REGEX.test(line)) {
        const nextTextLine = lines.slice(index + 1).find((nextLine) => nextLine.trim().length > 0);
        return Boolean(nextTextLine && hasDescriptionText(nextTextLine));
      }

      if (!DESCRIPTION_LEADING_MARKERS_REGEX.test(line)) return false;
      const textAfterIcons = line.replace(DESCRIPTION_LEADING_MARKERS_REGEX, "").trim();
      return hasDescriptionText(textAfterIcons);
    })
);

const PARTICULARITY_OPTIONS = [
  { key: "climatiseur", fr: "Climatiseur", en: "Air conditioner", ar: "مكيف" },
  { key: "chauffage", fr: "Chauffage", en: "Heating", ar: "تدفئة" },
  { key: "chaudiere", fr: "Chaudière", en: "Boiler", ar: "غلاية" },
  { key: "ascenseur", fr: "Ascenseur", en: "Elevator", ar: "مصعد" },
  { key: "logement_equipe", fr: "Logement équipé", en: "Fully equipped home", ar: "سكن مجهز" },
  { key: "vue_panoramique", fr: "Vue panoramique", en: "Panoramic view", ar: "إطلالة بانورامية" },
  { key: "vue_sur_mer", fr: "Vue sur mer", en: "Sea view", ar: "إطلالة على البحر" },
] as const;
const getTypeKeyFromLabel = (label?: string) => {
  if (!label) return "appartement";
  const normalized = label.trim().toLowerCase();
  const match = TYPE_OPTIONS.find((item) =>
    [item.fr, item.en, item.ar].some((value) => value.toLowerCase() === normalized)
  );
  return match?.key || "appartement";
};
const getParticularityKeyFromValue = (value?: string) => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  const match = PARTICULARITY_OPTIONS.find((item) =>
    [item.key, item.fr, item.en, item.ar].some((candidate) => candidate.toLowerCase() === normalized)
  );
  return match?.key || null;
};

const capitalizeFirstLetter = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const getTitleValidationError = (value: string, t: (key: string, options?: Record<string, unknown>) => string) => {
  const normalized = value.trim();
  if (!normalized) return t('addAds.titleRequired');
  if (normalized.length < TITLE_MIN_LENGTH) return t('addAds.titleMin', { count: TITLE_MIN_LENGTH });
  if (normalized.length > TITLE_MAX_LENGTH) return t('addAds.titleMax', { count: TITLE_MAX_LENGTH });
  return "";
};

const getDescriptionValidationError = (value: string, t: (key: string, options?: Record<string, unknown>) => string) => {
  const normalized = value.trim();
  if (!normalized) return t('addAds.descriptionRequired');
  if (normalized.length < DESCRIPTION_MIN_LENGTH) return t('addAds.descriptionMin', { count: DESCRIPTION_MIN_LENGTH });
  if (normalized.length > DESCRIPTION_MAX_LENGTH) return t('addAds.descriptionMax', { count: DESCRIPTION_MAX_LENGTH });
  return "";
};

const getFloorValidationError = (
  type: string,
  value: number | "",
  t: (key: string, options?: Record<string, unknown>) => string
) => {
  if (type !== "appartement") return "";
  if (value === "" || value === null || value === undefined) return t('addAds.floorCountRequired');
  return "";
};

const buildCompressedPhotoName = (name: string) => {
  const baseName = name.replace(/\.[^.]+$/, "") || "photo";
  return `${baseName}.jpg`;
};

const loadPhotoElement = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("IMAGE_LOAD_FAILED"));
    };
    image.src = objectUrl;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, quality: number) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("IMAGE_COMPRESSION_FAILED"));
      },
      "image/jpeg",
      quality
    );
  });

const compressPhotoFile = async (
  file: File,
  options: { maxWidth: number; maxHeight: number; quality: number; targetBytes: number }
) => {
  const image = await loadPhotoElement(file);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return file;

  let maxWidth = options.maxWidth;
  let maxHeight = options.maxHeight;
  let blob: Blob | null = null;

  for (let resizeAttempt = 0; resizeAttempt < 5; resizeAttempt += 1) {
    const scale = Math.min(1, maxWidth / image.naturalWidth, maxHeight / image.naturalHeight);
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));

    canvas.width = width;
    canvas.height = height;
    context.fillStyle = "#fff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    let quality = options.quality;
    blob = await canvasToBlob(canvas, quality);
    while (blob.size > options.targetBytes && quality > 0.34) {
      quality = Math.max(0.34, quality - 0.08);
      blob = await canvasToBlob(canvas, quality);
    }

    if (blob.size <= options.targetBytes || (width <= 720 && height <= 720)) break;
    maxWidth = Math.max(720, Math.round(maxWidth * 0.78));
    maxHeight = Math.max(720, Math.round(maxHeight * 0.78));
  }

  if (!blob) return file;
  if (file.size <= options.targetBytes && blob.size >= file.size) return file;

  return new File([blob], buildCompressedPhotoName(file.name), {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
};

interface ManualAddressFormData {
  address: string;
  wilayaId: string;
  communeId: string;
  coordinates?: GeocodeAddressResult;
}

const DEFAULT_LEAFLET_ICON = L.icon({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

const MANUAL_ADDRESS_MIN_LENGTH = 9;
const ALGERIA_COORDINATE_BOUNDS = {
  minLatitude: 18.8,
  maxLatitude: 37.3,
  minLongitude: -8.8,
  maxLongitude: 12.1,
};
const ALGERIA_LAT_LNG_BOUNDS = L.latLngBounds(
  [ALGERIA_COORDINATE_BOUNDS.minLatitude, ALGERIA_COORDINATE_BOUNDS.minLongitude],
  [ALGERIA_COORDINATE_BOUNDS.maxLatitude, ALGERIA_COORDINATE_BOUNDS.maxLongitude]
);
const AREA_MAX_DIGITS = 4;
const AREA_MAX_VALUE = 9999;
const PRICE_MAX_DIGITS_BY_CURRENCY: Record<string, number> = {
  DZD: 5,
  EUR: 3,
  USD: 3,
  GBP: 3,
};
const ADDRESS_MODAL_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 280,
    },
  },
};

const getPriceMaxDigits = (currency: string | undefined) =>
  PRICE_MAX_DIGITS_BY_CURRENCY[String(currency || "").toUpperCase()] ?? 3;

const normalizePriceInput = (value: unknown, currency: string | undefined) => {
  const digitsOnly = String(value || "").replace(/\D/g, "").slice(0, getPriceMaxDigits(currency));
  return digitsOnly === "" ? "" : Number(digitsOnly);
};

const getPriceValidationError = (price: number | "", currency: string | "", t: any) => {
  if (!price || isNaN(Number(price))) return t("addAds.priceRequired");
  const maxDigits = getPriceMaxDigits(currency);
  if (String(Math.trunc(Number(price))).length > maxDigits) {
    return t("addAds.priceMaxDigits", {
      count: maxDigits,
      defaultValue: `Le prix ne doit pas depasser ${maxDigits} chiffres.`,
    });
  }
  return "";
};

const normalizeAddressSegment = (value: string): string =>
  String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .toLowerCase();

const cleanRepeatedAddressSegments = (value: string): string => {
  const rawAddress = String(value || "").trim();
  if (!rawAddress) return "";

  const seenSegments = new Set<string>();
  const cleanedSegments: string[] = [];
  let hasReachedCountry = false;

  rawAddress
    .split(",")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .forEach((segment) => {
      const normalizedSegment = normalizeAddressSegment(segment);
      if (!normalizedSegment) return;
      if (hasReachedCountry) return;
      if (seenSegments.has(normalizedSegment)) return;

      cleanedSegments.push(segment);
      seenSegments.add(normalizedSegment);

      if (normalizedSegment === "algerie" || normalizedSegment === "algeria") {
        hasReachedCountry = true;
      }
    });

  return cleanedSegments.join(", ");
};

const cleanGeneratedManualAddress = (
  value: string,
  options: { communeName?: string; wilayaName?: string; postCode?: string } = {}
): string => {
  const rawAddress = cleanRepeatedAddressSegments(value);
  if (!rawAddress) return "";

  const communeName = normalizeAddressSegment(options.communeName || "");
  const wilayaName = normalizeAddressSegment(options.wilayaName || "");
  const postCode = String(options.postCode || "").trim();

  return rawAddress
    .split(",")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .filter((segment) => {
      const normalizedSegment = normalizeAddressSegment(segment);
      if (!normalizedSegment) return false;
      if (normalizedSegment === "algerie" || normalizedSegment === "algeria") return false;
      if (postCode && segment.includes(postCode)) return false;
      if (/^\d{4,6}$/.test(segment)) return false;
      if (/\b\d{4,6}\b/.test(segment) && normalizedSegment.split(" ").length <= 3) return false;
      if (communeName && normalizedSegment === communeName) return false;
      if (wilayaName && normalizedSegment === wilayaName) return false;
      if (communeName && normalizedSegment === `daira ${communeName}`) return false;
      if (wilayaName && normalizedSegment === `wilaya ${wilayaName}`) return false;
      return true;
    })
    .join(", ");
};

const buildReverseGeocodeAddress = (result: any) => {
  const displayName = typeof result?.display_name === "string" ? result.display_name.trim() : "";
  if (displayName) return cleanRepeatedAddressSegments(displayName);

  const address = result?.address && typeof result.address === "object" ? result.address : {};
  return cleanRepeatedAddressSegments([
    address.road,
    address.neighbourhood || address.suburb || address.quarter,
    address.city || address.town || address.village || address.municipality,
    address.state,
    address.country,
  ]
    .filter((part) => typeof part === "string" && part.trim())
    .join(", ")
    .trim());
};

const normalizeCountryText = (value: unknown) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "")
    .toLowerCase();

const isCoordinatesInsideAlgeria = (coordinates: GeocodeAddressResult) => {
  const latitude = Number(coordinates?.latitude);
  const longitude = Number(coordinates?.longitude);
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= ALGERIA_COORDINATE_BOUNDS.minLatitude &&
    latitude <= ALGERIA_COORDINATE_BOUNDS.maxLatitude &&
    longitude >= ALGERIA_COORDINATE_BOUNDS.minLongitude &&
    longitude <= ALGERIA_COORDINATE_BOUNDS.maxLongitude
  );
};

const isNominatimResultInAlgeria = (result: any) => {
  const address = result?.address && typeof result.address === "object" ? result.address : {};
  const countryCode = normalizeCountryText(address.country_code);
  const country = normalizeCountryText(address.country);
  return countryCode === "dz" || countryCode === "dza" || country === "algerie" || country === "algeria";
};

const reverseGeocodeWithNominatim = async (coordinates: GeocodeAddressResult) => {
  if (!isCoordinatesInsideAlgeria(coordinates)) {
    throw new Error("ADDRESS_OUTSIDE_ALGERIA");
  }

  const params = new URLSearchParams({
    lat: String(coordinates.latitude),
    lon: String(coordinates.longitude),
    format: "jsonv2",
    zoom: "18",
    addressdetails: "1",
    "accept-language": "fr",
  });
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 8000);

  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params.toString()}`, {
      headers: {
        Accept: "application/json",
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error("NOMINATIM_REVERSE_FAILED");
    }

    const result = await response.json();
    if (!isNominatimResultInAlgeria(result)) {
      throw new Error("ADDRESS_OUTSIDE_ALGERIA");
    }
    return buildReverseGeocodeAddress(result);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const AddAdPage: React.FC = () => {
  const { t } = useTranslation();
  const sourceLanguage = (i18n.resolvedLanguage || "fr").split("-")[0] as "fr" | "en" | "ar";
  const typeByKey = Object.fromEntries(TYPE_OPTIONS.map((item) => [item.key, item])) as Record<string, (typeof TYPE_OPTIONS)[number]>;
  const MAX_PHOTOS = 8;
  const PHOTO_COMPRESSION_TARGET_BYTES = 700 * 1024;
  const PHOTO_COMPRESSION_MAX_WIDTH = 1280;
  const PHOTO_COMPRESSION_MAX_HEIGHT = 1280;
  const PHOTO_COMPRESSION_QUALITY = 0.74;
  const MAX_PHOTOS_ERROR = t('addAds.maxPhotosError', { count: MAX_PHOTOS });
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const { loading, currentAd } = useSelector((state: RootState) => state.ads);
  const { user } = useSelector((state: RootState) => state.auth);
  const memoryAddressOptions = useSelector((state: RootState) => state.addressMemory.items);
  const isEditingAdLoading = Boolean(isEditMode && loading && !currentAd);

  // 🔹 Nouveaux champs
  const [errorPhone, setErrorPhone] = useState<string>();
  const [phoneMaskDigits, setPhoneMaskDigits] = useState<number | null>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [isCompressingPhotos, setIsCompressingPhotos] = useState(false);
  const [submittingAd, setSubmittingAd] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [createdAdNotice, setCreatedAdNotice] = useState<{ adId: string; moderationContactUserId?: string } | null>(null);
  const [captchaChallenge, setCaptchaChallenge] = useState<CaptchaChallenge>(createCaptchaChallenge);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [photoPreviews, setPhotoPreviews] = useState<Array<{ key: string; name: string; url: string; source: "existing" | "new" }>>([]);
  const [isPriorityPhotoSelectionEnabled, setIsPriorityPhotoSelectionEnabled] = useState(false);
  const [selectedPriorityPhotoKey, setSelectedPriorityPhotoKey] = useState<string | null>(null);
  const [isAddressSelectionValid, setIsAddressSelectionValid] = useState(false);
  const [isAddAddressModalOpen, setIsAddAddressModalOpen] = useState(false);
  const [manualAddressForm, setManualAddressForm] = useState<ManualAddressFormData>({
    address: "",
    wilayaId: "",
    communeId: "",
  });
  const [manualAddressError, setManualAddressError] = useState("");
  const [wilayaOptions, setWilayaOptions] = useState<WilayaOption[]>([]);
  const [communeOptions, setCommuneOptions] = useState<CommuneOption[]>([]);
  const [isWilayaOptionsLoading, setIsWilayaOptionsLoading] = useState(false);
  const [isCommuneOptionsLoading, setIsCommuneOptionsLoading] = useState(false);
  const [isCreatingManualAddress, setIsCreatingManualAddress] = useState(false);
  const [pendingManualAddress, setPendingManualAddress] = useState<ManualAddressFormData | null>(null);
  const [selectedAddressSuggestion, setSelectedAddressSuggestion] = useState<AddressSuggestion | null>(null);
  const [isTestingManualAddressGeocode, setIsTestingManualAddressGeocode] = useState(false);
  const [isOpeningManualAddressMap, setIsOpeningManualAddressMap] = useState(false);
  const [isReverseGeocodingManualAddress, setIsReverseGeocodingManualAddress] = useState(false);
  const [manualAddressGeocodeResult, setManualAddressGeocodeResult] = useState<GeocodeAddressResult | null>(null);
  const [hasManualAddressMarkerMoved, setHasManualAddressMarkerMoved] = useState(false);
  const [manualAddressGeocodeError, setManualAddressGeocodeError] = useState("");
  const manualAddressMapContainerRef = useRef<HTMLDivElement | null>(null);
  const manualAddressMapRef = useRef<L.Map | null>(null);
  const manualAddressMarkerRef = useRef<L.Marker | null>(null);

  // Active l’affichage des erreurs après soumission 
  const [submitted, setSubmitted] = useState(false);

  interface FormDatas {
    title: string;
    content: string;
    price: number | "";
    min_nights: number;
    room_nbr: number | "";
    nbr_floors: number | "";
    nbr_bathroom: number | "";
    nbr_kitchen: number | "";
    desactiver_commentaire: boolean;
    descriptionIconListEnabled: boolean;
    particularites: string[];
    area: number | "";
    adress: string;
    type: string;
    currency: Currency | "";
    email: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName: string;
    phone: string;
    codeCountry: string;
    indicatif: string;
    country: string;
  }

  const [formData, setFormData] = useState<FormDatas>({
    title: '',
    content: '',
    price: '',
    min_nights: 1,
    room_nbr: '',
    nbr_floors: '',
    nbr_bathroom: '',
    nbr_kitchen: '',
    desactiver_commentaire: false,
    descriptionIconListEnabled: false,
    particularites: [],
    area: '',
    adress: '',
    type: '',
    currency: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    codeCountry: '',
    indicatif: '',
    country: '',
  });
  const refreshCaptchaChallenge = useCallback(async (errorMessage = "") => {
    setCaptchaInput("");
    setCaptchaError(errorMessage);
    try {
      const challenge = await getCaptchaChallengeApi();
      setCaptchaChallenge(challenge);
    } catch {
      setCaptchaChallenge(createCaptchaChallenge());
      setCaptchaError(errorMessage || t('system.serverError'));
    }
  }, [t]);

  useEffect(() => {
    dispatch(resetAdsState());
  }, [dispatch]);

  useEffect(() => {
    if (!id) return;
    void dispatch(fetchAdById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (isEditMode) return;

    setFormData({
      title: '',
      content: '',
      price: '',
      min_nights: 1,
      room_nbr: '',
      nbr_floors: '',
      nbr_bathroom: '',
      nbr_kitchen: '',
      desactiver_commentaire: false,
      descriptionIconListEnabled: false,
      particularites: [],
      area: '',
      adress: '',
      type: '',
      currency: '',
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phone: '',
      codeCountry: '',
      indicatif: '',
      country: '',
    });
    setSubmitted(false);
    setErrorPhone("");
    setPhoneMaskDigits(null);
    setSelectedPhotos([]);
    setUploadProgress(0);
    setUploadingPhotos(false);
    setSubmittingAd(false);
    setUploadError(null);
    setCreatedAdNotice(null);
    setIsAddressSelectionValid(false);
    setSelectedAddressSuggestion(null);
    setIsAddAddressModalOpen(false);
    setManualAddressError("");
    setManualAddressForm({
      address: "",
      wilayaId: "",
      communeId: "",
    });
    setCommuneOptions([]);
    void refreshCaptchaChallenge();
    setPhotoPreviews((prev) => {
      prev.forEach((preview) => {
        if (preview.source === "new" && preview.url.startsWith("blob:")) {
          URL.revokeObjectURL(preview.url);
        }
      });
      return [];
    });
    setIsPriorityPhotoSelectionEnabled(false);
    setSelectedPriorityPhotoKey(null);
    dispatch(resetAdsState());
  }, [dispatch, isEditMode, refreshCaptchaChallenge]);

  useEffect(() => {
    void refreshCaptchaChallenge();
  }, [refreshCaptchaChallenge]);

  useEffect(() => {
    if (!currentAd || !isEditMode) return;

    setFormData((prev) => ({
      ...prev,
      title: getLocalizedText(currentAd.title_i18n, sourceLanguage),
      content: getLocalizedText(currentAd.content_i18n, sourceLanguage),
      price: currentAd.price ?? 0,
      min_nights: Math.min(5, Math.max(1, Number((currentAd as any).min_nights) || 1)),
      room_nbr: currentAd.room_nbr ?? 0,
      nbr_floors: Number.isInteger(Number((currentAd as any).nbr_floors)) ? Number((currentAd as any).nbr_floors) : '',
      nbr_bathroom: Number.isInteger(Number(currentAd.nbr_bathroom)) ? Number(currentAd.nbr_bathroom) : '',
      nbr_kitchen: Number.isInteger(Number(currentAd.nbr_kitchen)) ? Number(currentAd.nbr_kitchen) : '',
      desactiver_commentaire: Boolean((currentAd as any).desactiver_commentaire),
      descriptionIconListEnabled: Boolean((currentAd as any).descriptionIconListEnabled),
      particularites: Array.isArray((currentAd as any).particularites)
        ? (currentAd as any).particularites
            .map((value: string) => getParticularityKeyFromValue(value))
            .filter((value: string | null): value is string => Boolean(value))
        : [],
      area: currentAd.area ?? 0,
      adress: currentAd.adress || "",
      type: getTypeKeyFromLabel(currentAd.type),
      currency: currentAd.currency || Currenci.EUR,
    }));
    setIsAddressSelectionValid(Boolean(currentAd.adress));
    const currentQuartier = (currentAd as any)?.idQuartier;
    const currentQuartierId =
      typeof currentQuartier === "string"
        ? currentQuartier
        : currentQuartier?._id || currentQuartier?.id || "";
    setSelectedAddressSuggestion(
      currentQuartierId && currentAd.adress
        ? {
            id: `quartier-${currentQuartierId}`,
            source: "quartier",
            label: currentAd.adress,
            quartierId: String(currentQuartierId),
          }
        : null
    );
    setPendingManualAddress(null);

    setSelectedPhotos([]);
    setIsPriorityPhotoSelectionEnabled(false);
    setSelectedPriorityPhotoKey(null);
    setPhotoPreviews(
      Array.isArray(currentAd.photos)
        ? currentAd.photos
            .filter((photo): photo is string => typeof photo === "string")
            .map((url, index) => ({
              key: `${url}-${index}`,
              name: t('addAds.existingPhoto', { index: index + 1 }),
              url,
              source: "existing" as const,
            }))
        : []
    );
  }, [currentAd, isEditMode, sourceLanguage, t]);

  useEffect(() => {
    if (!isAddAddressModalOpen || wilayaOptions.length > 0) return;

    let isMounted = true;
    const loadWilayas = async () => {
      setIsWilayaOptionsLoading(true);
      try {
        const response = await listWilayasApi();
        if (!isMounted) return;
        setWilayaOptions(Array.isArray(response?.data?.data) ? response.data.data : []);
      } catch {
        if (!isMounted) return;
        // Échouer silencieusement sans afficher d'erreur
        setWilayaOptions([]);
      } finally {
        if (isMounted) {
          setIsWilayaOptionsLoading(false);
        }
      }
    };

    void loadWilayas();
    return () => {
      isMounted = false;
    };
  }, [isAddAddressModalOpen, wilayaOptions.length]);

  useEffect(() => {
    if (!isAddAddressModalOpen) return;
    if (!manualAddressForm.wilayaId) {
      setCommuneOptions([]);
      return;
    }

    let isMounted = true;
    const loadCommunes = async () => {
      setIsCommuneOptionsLoading(true);
      try {
        const response = await listCommunesByWilayaApi(manualAddressForm.wilayaId);
        if (!isMounted) return;
        const nextCommunes = Array.isArray(response?.data?.data) ? response.data.data : [];
        setCommuneOptions(nextCommunes);
        setManualAddressForm((prev) => {
          if (!prev.communeId) return prev;
          const currentStillExists = nextCommunes.some((item) => item.id === prev.communeId);
          return currentStillExists ? prev : { ...prev, communeId: "" };
        });
      } catch {
        if (!isMounted) return;
        setCommuneOptions([]);
        setManualAddressError(t('addAds.addressModalLoadCommunesError'));
      } finally {
        if (isMounted) {
          setIsCommuneOptionsLoading(false);
        }
      }
    };

    void loadCommunes();
    return () => {
      isMounted = false;
    };
  }, [isAddAddressModalOpen, manualAddressForm.wilayaId, t]);

  const isManualAddressFormValid =
    manualAddressForm.address.trim().length >= MANUAL_ADDRESS_MIN_LENGTH &&
    Boolean(manualAddressForm.wilayaId) &&
    Boolean(manualAddressForm.communeId);
  const canOpenManualAddressMap = Boolean(manualAddressForm.wilayaId && manualAddressForm.communeId);

  const buildManualAddressLabel = useCallback(() => {
    const selectedWilaya = wilayaOptions.find((option) => option.id === manualAddressForm.wilayaId);
    const selectedCommune = communeOptions.find((option) => option.id === manualAddressForm.communeId);
    const normalizedAddress = cleanGeneratedManualAddress(manualAddressForm.address, {
      communeName: selectedCommune?.name,
      wilayaName: selectedWilaya?.name,
      postCode: selectedWilaya?.postCode,
    });
    if (!normalizedAddress || !selectedWilaya || !selectedCommune) return normalizedAddress;
    const normalizedLabel = normalizedAddress.toLowerCase();
    if (normalizedLabel.includes(selectedCommune.name.toLowerCase()) && normalizedLabel.includes(selectedWilaya.name.toLowerCase())) {
      return normalizedAddress;
    }
    return `${normalizedAddress}, ${selectedCommune.name}, ${selectedWilaya.name} ${selectedWilaya.postCode}, Algérie`;
  }, [communeOptions, manualAddressForm.address, manualAddressForm.communeId, manualAddressForm.wilayaId, wilayaOptions]);

  const updateManualAddressFromCoordinates = useCallback(async (coordinates: GeocodeAddressResult) => {
    setIsReverseGeocodingManualAddress(true);
    setManualAddressGeocodeError("");
    try {
      if (!isCoordinatesInsideAlgeria(coordinates)) {
        throw new Error("ADDRESS_OUTSIDE_ALGERIA");
      }

      let generatedAddress = "";
      try {
        generatedAddress = await reverseGeocodeWithNominatim(coordinates);
      } catch {
        const response = await reverseGeocodeCoordinatesApi(coordinates);
        const reverseResult = response?.data?.data;
        generatedAddress = typeof reverseResult?.address === "string" ? reverseResult.address.trim() : "";
      }
      const selectedWilaya = wilayaOptions.find((option) => option.id === manualAddressForm.wilayaId);
      const selectedCommune = communeOptions.find((option) => option.id === manualAddressForm.communeId);
      generatedAddress = cleanGeneratedManualAddress(generatedAddress, {
        communeName: selectedCommune?.name,
        wilayaName: selectedWilaya?.name,
        postCode: selectedWilaya?.postCode,
      });

      const latitude = Number(coordinates.latitude);
      const longitude = Number(coordinates.longitude);

      if (!generatedAddress || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        throw new Error("REVERSE_GEOCODE_NOT_FOUND");
      }

      setManualAddressForm((prev) => ({
        ...prev,
        address: generatedAddress,
      }));
      setManualAddressGeocodeResult({ latitude, longitude });
    } catch (error: any) {
      setManualAddressGeocodeResult(coordinates);
      setManualAddressGeocodeError(
        error?.message === "ADDRESS_OUTSIDE_ALGERIA"
          ? t('addAds.addressModalAlgeriaOnly')
          : t('addAds.addressModalReverseGeocodeError', {
              defaultValue: "Impossible de generer l'adresse textuelle depuis ce point. Deplacez legerement le pointeur ou saisissez l'adresse.",
            })
      );
    } finally {
      setIsReverseGeocodingManualAddress(false);
    }
  }, [communeOptions, manualAddressForm.communeId, manualAddressForm.wilayaId, t, wilayaOptions]);

  const handleManualAddressMarkerDragEnd = useCallback(() => {
    const marker = manualAddressMarkerRef.current;
    if (!marker) return;

    const nextLatLng = marker.getLatLng();
    const coordinates = {
      latitude: nextLatLng.lat,
      longitude: nextLatLng.lng,
    };
    if (!isCoordinatesInsideAlgeria(coordinates)) {
      setManualAddressGeocodeError(t('addAds.addressModalAlgeriaOnly'));
      return;
    }
    setHasManualAddressMarkerMoved(true);
    setManualAddressGeocodeResult(coordinates);
    void updateManualAddressFromCoordinates(coordinates);
  }, [t, updateManualAddressFromCoordinates]);

  const moveManualAddressMarkerTo = useCallback((latLng: L.LatLng) => {
    const coordinates = {
      latitude: latLng.lat,
      longitude: latLng.lng,
    };

    if (!isCoordinatesInsideAlgeria(coordinates)) {
      setManualAddressGeocodeError(t('addAds.addressModalAlgeriaOnly'));
      return;
    }

    setHasManualAddressMarkerMoved(true);
    setManualAddressGeocodeResult(coordinates);
    if (manualAddressMarkerRef.current) {
      manualAddressMarkerRef.current.setLatLng(latLng);
    }
    void updateManualAddressFromCoordinates(coordinates);
  }, [t, updateManualAddressFromCoordinates]);

  useEffect(() => {
    if (!isAddAddressModalOpen || !manualAddressGeocodeResult || !manualAddressMapContainerRef.current) {
      return;
    }

    const latLng: L.LatLngTuple = [manualAddressGeocodeResult.latitude, manualAddressGeocodeResult.longitude];

    if (!manualAddressMapRef.current) {
      manualAddressMapRef.current = L.map(manualAddressMapContainerRef.current, {
        center: latLng,
        zoom: 15,
        doubleClickZoom: false,
        scrollWheelZoom: false,
        maxBounds: ALGERIA_LAT_LNG_BOUNDS,
        maxBoundsViscosity: 1,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(manualAddressMapRef.current);
      manualAddressMapRef.current.on("dblclick", (event: L.LeafletMouseEvent) => {
        moveManualAddressMarkerTo(event.latlng);
      });
    }

    const map = manualAddressMapRef.current;
    if (manualAddressMarkerRef.current) {
      manualAddressMarkerRef.current.setLatLng(latLng);
    } else {
      manualAddressMarkerRef.current = L.marker(latLng, { icon: DEFAULT_LEAFLET_ICON, draggable: true }).addTo(map);
      manualAddressMarkerRef.current.on("dragend", handleManualAddressMarkerDragEnd);
    }
    manualAddressMarkerRef.current.bindPopup(buildManualAddressLabel());
    map.setView(latLng, 15);
    setTimeout(() => map.invalidateSize(), 0);
  }, [buildManualAddressLabel, handleManualAddressMarkerDragEnd, isAddAddressModalOpen, manualAddressGeocodeResult, moveManualAddressMarkerTo]);

  useEffect(() => {
    if (isAddAddressModalOpen) return;
    if (manualAddressMapRef.current) {
      manualAddressMapRef.current.remove();
      manualAddressMapRef.current = null;
      manualAddressMarkerRef.current = null;
    }
  }, [isAddAddressModalOpen]);

  useEffect(() => {
    if (manualAddressGeocodeResult) return;
    if (manualAddressMapRef.current) {
      manualAddressMapRef.current.remove();
      manualAddressMapRef.current = null;
      manualAddressMarkerRef.current = null;
    }
  }, [manualAddressGeocodeResult]);

  const getFileKey = (file: File) => `${file.name}-${file.lastModified}`;

  const closeAddAddressModal = () => {
    setIsAddAddressModalOpen(false);
    setManualAddressError("");
    setManualAddressGeocodeResult(null);
    setManualAddressGeocodeError("");
    setIsTestingManualAddressGeocode(false);
    setIsOpeningManualAddressMap(false);
    setIsReverseGeocodingManualAddress(false);
    setHasManualAddressMarkerMoved(false);
    setManualAddressForm({
      address: "",
      wilayaId: "",
      communeId: "",
    });
    setCommuneOptions([]);
  };

  const openAddAddressModal = (prefillAddress = "") => {
    setManualAddressError("");
    setManualAddressGeocodeResult(null);
    setManualAddressGeocodeError("");
    setIsTestingManualAddressGeocode(false);
    setIsOpeningManualAddressMap(false);
    setIsReverseGeocodingManualAddress(false);
    setHasManualAddressMarkerMoved(false);
    setManualAddressForm({
      address: prefillAddress.trim(),
      wilayaId: "",
      communeId: "",
    });
    setCommuneOptions([]);
    setIsAddAddressModalOpen(true);
  };

  const handleManualAddressFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setManualAddressError("");
    setManualAddressGeocodeResult(null);
    setManualAddressGeocodeError("");
    setIsOpeningManualAddressMap(false);
    setIsReverseGeocodingManualAddress(false);
    setHasManualAddressMarkerMoved(false);
    setManualAddressForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "wilayaId" ? { communeId: "" } : {}),
    }));
  };

  const handleTestManualAddressGeocode = async () => {
    if (!isManualAddressFormValid) {
      setManualAddressError(t('addAds.addressModalRequired'));
      return;
    }

    setManualAddressError("");
    setManualAddressGeocodeError("");
    setManualAddressGeocodeResult(null);
    setIsTestingManualAddressGeocode(true);
    try {
      const response = await geocodeAddressApi(buildManualAddressLabel());
      const coordinates = response?.data?.data;
      const latitude = Number(coordinates?.latitude);
      const longitude = Number(coordinates?.longitude);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || (latitude === 0 && longitude === 0)) {
        throw new Error("GEOCODE_NOT_FOUND");
      }
      if (!isCoordinatesInsideAlgeria({ latitude, longitude })) {
        throw new Error("ADDRESS_OUTSIDE_ALGERIA");
      }

      setManualAddressGeocodeResult({ latitude, longitude });
    } catch (error: any) {
      setManualAddressGeocodeError(
        error?.message === "ADDRESS_OUTSIDE_ALGERIA"
          ? t('addAds.addressModalAlgeriaOnly')
          : t('addAds.addressModalGeocodeNotRecognized')
      );
    } finally {
      setIsTestingManualAddressGeocode(false);
    }
  };

  const handleOpenManualAddressMap = async () => {
    if (!canOpenManualAddressMap) {
      setManualAddressError(t('addAds.addressModalMapNeedsLocation', {
        defaultValue: "La ville et la commune sont requises pour utiliser la carte.",
      }));
      return;
    }

    const selectedWilaya = wilayaOptions.find((option) => option.id === manualAddressForm.wilayaId);
    const selectedCommune = communeOptions.find((option) => option.id === manualAddressForm.communeId);
    if (!selectedWilaya || !selectedCommune) {
      setManualAddressError(t('addAds.addressModalRequired'));
      return;
    }

    setManualAddressError("");
    setManualAddressGeocodeError("");
    setIsOpeningManualAddressMap(true);
    try {
      const response = await geocodeAddressApi(`${selectedCommune.name}, ${selectedWilaya.name} ${selectedWilaya.postCode}, Algerie`);
      const coordinates = response?.data?.data;
      const latitude = Number(coordinates?.latitude);
      const longitude = Number(coordinates?.longitude);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || (latitude === 0 && longitude === 0)) {
        throw new Error("GEOCODE_NOT_FOUND");
      }
      if (!isCoordinatesInsideAlgeria({ latitude, longitude })) {
        throw new Error("ADDRESS_OUTSIDE_ALGERIA");
      }

      const nextCoordinates = { latitude, longitude };
      setManualAddressGeocodeResult(nextCoordinates);
      setHasManualAddressMarkerMoved(false);
    } catch (error: any) {
      setManualAddressGeocodeError(
        error?.message === "ADDRESS_OUTSIDE_ALGERIA"
          ? t('addAds.addressModalAlgeriaOnly')
          : t('addAds.addressModalGeocodeNotRecognized')
      );
    } finally {
      setIsOpeningManualAddressMap(false);
    }
  };

  const handleCreateManualAddress = async () => {
    if (!isManualAddressFormValid) {
      setManualAddressError(
        hasManualAddressMarkerMoved
          ? t('addAds.addressModalReverseGeocodeError', {
              defaultValue: "Impossible de generer l'adresse textuelle depuis ce point. Deplacez legerement le pointeur ou saisissez l'adresse.",
            })
          : t('addAds.addressModalRequired')
      );
      return;
    }

    setIsCreatingManualAddress(true);
    setManualAddressError("");
    try {
      const selectedWilaya = wilayaOptions.find((option) => option.id === manualAddressForm.wilayaId);
      const selectedCommune = communeOptions.find((option) => option.id === manualAddressForm.communeId);

      if (!selectedWilaya || !selectedCommune) {
        throw new Error('Address dependencies missing');
      }

      const normalizedAddress = cleanGeneratedManualAddress(manualAddressForm.address, {
        communeName: selectedCommune.name,
        wilayaName: selectedWilaya.name,
        postCode: selectedWilaya.postCode,
      });

      if (normalizedAddress.length < MANUAL_ADDRESS_MIN_LENGTH) {
        setManualAddressError(t('addAds.addressModalRequired'));
        return;
      }
      if (manualAddressGeocodeResult && !isCoordinatesInsideAlgeria(manualAddressGeocodeResult)) {
        setManualAddressError(t('addAds.addressModalAlgeriaOnly'));
        return;
      }

      const manualAddressLabel = `${normalizedAddress}, ${selectedCommune.name}, ${selectedWilaya.name} ${selectedWilaya.postCode}, Algérie`;

      const suggestion: AddressSuggestion = {
        id: `manual-${Date.now()}`,
        source: 'quartier',
        label: `${normalizedAddress}, ${selectedCommune.name}, ${selectedWilaya.name} ${selectedWilaya.postCode}, Algérie`,
        address: normalizedAddress,
        communeId: manualAddressForm.communeId,
        wilayaId: manualAddressForm.wilayaId,
      };
      suggestion.label = manualAddressLabel;
      if (manualAddressGeocodeResult) {
        suggestion.coordinates = manualAddressGeocodeResult;
      }

      setFormData((prev) => ({ ...prev, adress: suggestion.label }));
      setPendingManualAddress({
        address: normalizedAddress,
        wilayaId: manualAddressForm.wilayaId,
        communeId: manualAddressForm.communeId,
        ...(manualAddressGeocodeResult && { coordinates: manualAddressGeocodeResult }),
      });
      setIsAddressSelectionValid(true);
      setSelectedAddressSuggestion(suggestion);
      dispatch(addAddressMemoryItem(suggestion));
      closeAddAddressModal();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        t('addAds.addressModalCreateError');
      setManualAddressError(message);
    } finally {
      setIsCreatingManualAddress(false);
    }
  };

  const handleChange = (event: any) => {
    //console.log("test:",event.target.value);
    const target = event.target;
    const name = target.name;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const mask = target.mask || '';
    const indicatif = target.indicatif || '';
    setFormData({
      ...formData,
      indicatif: indicatif,
      country: target.country,
      codeCountry: target.code
    });

    let expectedDigits: number | null = null;
    if (mask) {
      expectedDigits = (mask.match(/\d/g) || []).length;
      if (expectedDigits !== phoneMaskDigits) setPhoneMaskDigits(expectedDigits);
    }

    setFormData((prev) => {
      if (name === "area") {
        const digitsOnly = String(value || "").replace(/\D/g, "").slice(0, AREA_MAX_DIGITS);
        return {
          ...prev,
          area: digitsOnly === "" ? "" : Math.min(Number(digitsOnly), AREA_MAX_VALUE),
        };
      }

      if (name === "price") {
        return {
          ...prev,
          price: normalizePriceInput(value, prev.currency),
        };
      }

      if (name === "currency") {
        return {
          ...prev,
          currency: value,
          price: normalizePriceInput(prev.price, value),
        };
      }

      if (name === "min_nights") {
        return {
          ...prev,
          min_nights: Math.min(5, Math.max(1, Number(value) || 1)),
        };
      }

      if (name === "nbr_floors" || name === "nbr_bathroom" || name === "nbr_kitchen") {
        return {
          ...prev,
          [name]: value === "" ? "" : Number(value),
        };
      }

      if (name === "content") {
        const content = String(value || "");
        return {
          ...prev,
          content,
          descriptionIconListEnabled: hasManualIconListDescription(content),
        };
      }

      if (name === "particularites") {
        const checkboxValue = typeof target.value === "string" ? target.value : "";
        return {
          ...prev,
          particularites: target.checked
            ? Array.from(new Set([...prev.particularites, checkboxValue]))
            : prev.particularites.filter((item) => item !== checkboxValue),
        };
      }

      return {
        ...prev,
        [name]: value,
        ...(name === 'phone' ? { indicatif } : {}),
      };
    });

  }

  const hasValidGuestUserFields = (data: FormDatas) => {
    const requiredBase = [
      data.email,
      data.password,
      data.confirmPassword,
      data.firstName,
      data.lastName,
      data.phone,
      data.codeCountry,
      data.indicatif,
      data.country,
    ];

    const hasRequiredBase = requiredBase.every((value) => value !== null && value !== undefined && value !== '');
    return hasRequiredBase;
  };

  const handlePhotosChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;

    const nextSelected = [...selectedPhotos];
    const nextPreviews = [...photoPreviews];
    let overflow = false;

    setIsCompressingPhotos(true);

    for (const file of files) {
      if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
        continue;
      }

      if (nextPreviews.length >= MAX_PHOTOS) {
        overflow = true;
        continue;
      }

      let uploadFile = file;
      try {
        uploadFile = await compressPhotoFile(file, {
          maxWidth: PHOTO_COMPRESSION_MAX_WIDTH,
          maxHeight: PHOTO_COMPRESSION_MAX_HEIGHT,
          quality: PHOTO_COMPRESSION_QUALITY,
          targetBytes: PHOTO_COMPRESSION_TARGET_BYTES,
        });
      } catch {
        continue;
      }

      const fileKey = getFileKey(uploadFile);
      const alreadyExists = nextSelected.some((item) => getFileKey(item) === fileKey);
      if (alreadyExists) continue;

      nextSelected.push(uploadFile);
      nextPreviews.push({
        key: fileKey,
        name: uploadFile.name,
        url: URL.createObjectURL(uploadFile),
        source: "new",
      });
    }

    setSelectedPhotos(nextSelected);
    setPhotoPreviews(nextPreviews);
    const nextUploadError = overflow ? MAX_PHOTOS_ERROR : null;
    setUploadError(nextUploadError);
    if (nextUploadError) {
      toast.error(nextUploadError);
    }

    // Permet de re-sélectionner le même fichier et de relancer onChange
    event.target.value = "";
    setUploadProgress(0);
    setIsCompressingPhotos(false);
  };

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (uploadError !== MAX_PHOTOS_ERROR) return;

      const target = event.target as HTMLElement | null;
      if (target?.closest("#photos")) return;

      setUploadError(null);
    };

    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, [uploadError, MAX_PHOTOS_ERROR]);

  const handleRemoveSelectedPhoto = (fileKey: string) => {
    const removedPreview = photoPreviews.find((preview) => preview.key === fileKey);
    if (removedPreview?.source === "new" && removedPreview.url.startsWith("blob:")) {
      URL.revokeObjectURL(removedPreview.url);
    }

    setSelectedPhotos((prev) =>
      prev.filter((file) => getFileKey(file) !== fileKey)
    );
    setPhotoPreviews((prev) =>
      prev.filter((preview) => preview.key !== fileKey)
    );
    setSelectedPriorityPhotoKey((prev) => (prev === fileKey ? null : prev));
  };

  const handlePriorityPhotoChange = (fileKey: string, checked: boolean) => {
    setSelectedPriorityPhotoKey(checked ? fileKey : null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setUploadError(null);
    const resetCaptcha = async (errorMessage = "") => {
      await refreshCaptchaChallenge(errorMessage);
    };

    let hasFieldValidationError = false;
    const titleError = getTitleValidationError(formData.title, t);
    const descriptionError = getDescriptionValidationError(formData.content, t);
    const floorError = getFloorValidationError(formData.type, formData.nbr_floors, t);
    const priceError = getPriceValidationError(formData.price, formData.currency, t);

    if (titleError || descriptionError || floorError || priceError) {
      hasFieldValidationError = true;
    }

    if (!user) {
      if (!formData.phone.trim()) {
        setErrorPhone(t('addAds.phoneRequired'));
        hasFieldValidationError = true;
      } else if (hasLeadingZeroPhoneDigit(formData.phone)) {
        setErrorPhone(t('addAds.phoneLeadingZero'));
        hasFieldValidationError = true;
      } else if (phoneMaskDigits && phoneMaskDigits > 0) {
        const enteredDigits = formData.phone.replace(/\D/g, '').length;
        if (enteredDigits !== phoneMaskDigits) {
          setErrorPhone(t('addAds.phoneExact', { count: phoneMaskDigits }));
          hasFieldValidationError = true;
        } else setErrorPhone("");
      } else {
        setErrorPhone("");
      }
    } else {
      setErrorPhone("");
    }

    if (!formData.adress.trim() || !isAddressSelectionValid) {
      await resetCaptcha();
      return;
    }

    if (photoPreviews.length === 0) {
      setUploadError(t('addAds.photosRequired'));
      await resetCaptcha();
      return;
    }
    if (hasFieldValidationError) {
      await resetCaptcha();
      return;
    }
    if (!captchaInput.trim()) {
      await resetCaptcha(t('captcha.errorRequired'));
      return;
    }
    if (!captchaChallenge.proof) {
      await resetCaptcha(t('system.serverError'));
      return;
    }
    if (captchaInput.trim().toUpperCase() !== captchaChallenge.code.toUpperCase()) {
      await resetCaptcha(t('captcha.errorInvalid'));
      return;
    }
    setCaptchaError("");
    const captchaPayload = {
      input: captchaInput.trim(),
      code: captchaChallenge.code,
      proof: captchaChallenge.proof,
    };
    const manualAddressFromSelection =
      !resolvedSelectedQuartierId &&
      selectedAddressSuggestion?.source === 'quartier' &&
      selectedAddressSuggestion.address &&
      selectedAddressSuggestion.wilayaId &&
      selectedAddressSuggestion.communeId
        ? {
            address: selectedAddressSuggestion.address,
            wilayaId: selectedAddressSuggestion.wilayaId,
            communeId: selectedAddressSuggestion.communeId,
            ...(selectedAddressSuggestion.coordinates && { coordinates: selectedAddressSuggestion.coordinates }),
          }
        : null;
    const effectiveManualAddress = pendingManualAddress || manualAddressFromSelection;
    const photoPrioritaireIndex = selectedPriorityPhotoKey
      ? photoPreviews.findIndex((preview) => preview.key === selectedPriorityPhotoKey)
      : -1;

    setSubmittingAd(true);
    setUploadingPhotos(selectedPhotos.length > 0);
    setUploadProgress(selectedPhotos.length > 0 ? 100 : 0);
    try {
      const payloadParticularites = formData.particularites;
      const descriptionIconListEnabled = hasManualIconListDescription(formData.content);
      let submitAttempted = false;
      if (formData.title && formData.content && formData.price && formData.room_nbr && formData.area && formData.adress && formData.type && formData.currency) {
        const selectedType = typeByKey[formData.type] || typeByKey.appartement;
        if (isEditMode && id && user) {
          submitAttempted = true;
          try {
            const selectedType = typeByKey[formData.type] || typeByKey.appartement;
            const keptExistingPhotos = photoPreviews
              .filter((preview) => preview.source === "existing")
              .map((preview) => preview.url);

            const updatedAd = await dispatch(
              updateAd({
                id,
                data: {
                  ad: {
                    content_i18n: {
                      fr: sourceLanguage === "fr" ? formData.content : "",
                      en: sourceLanguage === "en" ? formData.content : "",
                      ar: sourceLanguage === "ar" ? formData.content : "",
                    },
                    title_i18n: {
                      fr: sourceLanguage === "fr" ? formData.title : "",
                      en: sourceLanguage === "en" ? formData.title : "",
                      ar: sourceLanguage === "ar" ? formData.title : "",
                    },
                    price: formData.price,
                    min_nights: formData.min_nights,
                    room_nbr: formData.room_nbr,
                    nbr_floors: formData.type === "appartement" ? (formData.nbr_floors === "" ? null : Number(formData.nbr_floors)) : null,
                    nbr_bathroom: formData.nbr_bathroom === "" ? null : Number(formData.nbr_bathroom),
                    nbr_kitchen: formData.nbr_kitchen === "" ? null : Number(formData.nbr_kitchen),
                    desactiver_commentaire: formData.desactiver_commentaire,
                    descriptionIconListEnabled,
                    particularites: payloadParticularites,
                    area: formData.area,
                    ...(resolvedSelectedQuartierId && { adressId: resolvedSelectedQuartierId }),
                    ...(effectiveManualAddress && { manualAddress: effectiveManualAddress }),
                    type: selectedType.fr,
                    currency: formData.currency,
                    photos: keptExistingPhotos,
                    photoFiles: selectedPhotos,
                    ...(photoPrioritaireIndex >= 0 && { photoPrioritaireIndex }),
                  } as Ad,
                  userId: user.id,
                },
              })
            ).unwrap();

            toast.success(t('addAds.updateSuccess'));
            setSubmitted(false);
            setSelectedPhotos([]);
            setSelectedPriorityPhotoKey(null);
            setUploadProgress(0);
            setPendingManualAddress(null);
            setSelectedAddressSuggestion(null);
            setPhotoPreviews(
              Array.isArray((updatedAd as any)?.photos)
                ? (updatedAd as any).photos
                    .filter((photo: unknown): photo is string => typeof photo === "string")
                    .map((url: string, index: number) => ({
                      key: `${url}-${index}`,
                      name: t('addAds.existingPhoto', { index: index + 1 }),
                      url,
                      source: "existing" as const,
                    }))
                : []
            );
            navigate(`/ads/${id}`);
            return;
          } catch (err) {
            const message = typeof err === "string" ? err : t('system.adsUpdateError');
            toast.error(message);
            await resetCaptcha();
            return;
          }
        }

        if (user) {
          submitAttempted = true;
          try {
            const createdAd = await dispatch(
              createAd(
                {
                  ad: {
                    content_i18n: { fr: sourceLanguage === "fr" ? formData.content : "", en: sourceLanguage === "en" ? formData.content : "", ar: sourceLanguage === "ar" ? formData.content : "" },
                    title_i18n: { fr: sourceLanguage === "fr" ? formData.title : "", en: sourceLanguage === "en" ? formData.title : "", ar: sourceLanguage === "ar" ? formData.title : "" },
                    price: formData.price,
                    min_nights: formData.min_nights,
                    room_nbr: formData.room_nbr,
                    nbr_floors: formData.type === "appartement" ? (formData.nbr_floors === "" ? null : Number(formData.nbr_floors)) : null,
                    nbr_bathroom: formData.nbr_bathroom === "" ? null : Number(formData.nbr_bathroom),
                    nbr_kitchen: formData.nbr_kitchen === "" ? null : Number(formData.nbr_kitchen),
                    desactiver_commentaire: formData.desactiver_commentaire,
                    descriptionIconListEnabled,
                    particularites: payloadParticularites,
                    area: formData.area,
                    ...(resolvedSelectedQuartierId && { adressId: resolvedSelectedQuartierId }),
                    ...(effectiveManualAddress && { manualAddress: effectiveManualAddress }),
                    type: selectedType.fr,
                    currency: formData.currency,
                    photoFiles: selectedPhotos,
                    ...(photoPrioritaireIndex >= 0 && { photoPrioritaireIndex }),
                  } as Ad,
                  userId: user.id,
                  captcha: captchaPayload,
                }
              )
            ).unwrap();
            await dispatch(fetchCurrentUser());

            toast.success(t('addAds.createSuccess'));
            if ((createdAd as any)?.id) {
              setCreatedAdNotice({
                adId: String((createdAd as any).id),
                moderationContactUserId: typeof (createdAd as any)?.moderationContactUserId === "string"
                  ? String((createdAd as any).moderationContactUserId)
                  : undefined,
              });
              setSelectedPhotos([]);
              setPhotoPreviews([]);
              setSelectedPriorityPhotoKey(null);
              setUploadProgress(0);
              setPendingManualAddress(null);
              setSelectedAddressSuggestion(null);
              return;
            }

            setSubmitted(false);
            setSelectedPhotos([]);
            setPhotoPreviews([]);
            setSelectedPriorityPhotoKey(null);
            setUploadProgress(0);
            setPendingManualAddress(null);
            setSelectedAddressSuggestion(null);
            await resetCaptcha();
          } catch (err) {
            const message = typeof err === "string" ? err : t('system.adsCreateError');
            toast.error(message);
            await resetCaptcha();
          }
        }
      } else if (hasValidGuestUserFields(formData) && !errorPhone) {
        submitAttempted = true;
        const selectedType = typeByKey[formData.type] || typeByKey.appartement;
        try {
          const createdAd = await dispatch(
            createAd(
              {
                ad: {
                  content_i18n: { fr: sourceLanguage === "fr" ? formData.content : "", en: sourceLanguage === "en" ? formData.content : "", ar: sourceLanguage === "ar" ? formData.content : "" },
                  title_i18n: { fr: sourceLanguage === "fr" ? formData.title : "", en: sourceLanguage === "en" ? formData.title : "", ar: sourceLanguage === "ar" ? formData.title : "" },
                  price: formData.price,
                  min_nights: formData.min_nights,
                  room_nbr: formData.room_nbr,
                  nbr_floors: formData.type === "appartement" ? (formData.nbr_floors === "" ? null : Number(formData.nbr_floors)) : null,
                  nbr_bathroom: formData.nbr_bathroom === "" ? null : Number(formData.nbr_bathroom),
                  nbr_kitchen: formData.nbr_kitchen === "" ? null : Number(formData.nbr_kitchen),
                  desactiver_commentaire: formData.desactiver_commentaire,
                  descriptionIconListEnabled,
                  particularites: payloadParticularites,
                  area: formData.area,
                  ...(resolvedSelectedQuartierId && { adressId: resolvedSelectedQuartierId }),
                  ...(effectiveManualAddress && { manualAddress: effectiveManualAddress }),
                  type: selectedType.fr,
                  currency: formData.currency,
                  photoFiles: selectedPhotos,
                  ...(photoPrioritaireIndex >= 0 && { photoPrioritaireIndex }),
                } as Ad,
                user: {
                  email: formData.email,
                  password: formData.password,
                  firstName: formData.firstName,
                  lastName: formData.lastName,
                  phone: formData.phone,
                  codeCountry: formData.codeCountry,
                  indicatif: formData.indicatif,
                  country: formData.country
                },
                captcha: captchaPayload,
              }
            )
          ).unwrap();

          toast.success(t('addAds.createSuccess'));
          if ((createdAd as any)?.id) {
            setCreatedAdNotice({
              adId: String((createdAd as any).id),
              moderationContactUserId: typeof (createdAd as any)?.moderationContactUserId === "string"
                ? String((createdAd as any).moderationContactUserId)
                : undefined,
            });
            setSelectedPhotos([]);
            setPhotoPreviews([]);
            setSelectedPriorityPhotoKey(null);
            setUploadProgress(0);
            setPendingManualAddress(null);
            setSelectedAddressSuggestion(null);
            return;
          }

          setSubmitted(false);
          setSelectedPhotos([]);
          setPhotoPreviews([]);
          setSelectedPriorityPhotoKey(null);
          setUploadProgress(0);
          setPendingManualAddress(null);
          setSelectedAddressSuggestion(null);
          await resetCaptcha();
        } catch (err) {
          const message = typeof err === "string" ? err : t('system.adsCreateError');
          toast.error(message);
          await resetCaptcha();
        }
      }
      if (!submitAttempted) {
        await resetCaptcha();
      }
    } finally {
      setSubmittingAd(false);
      setUploadingPhotos(false);
      setUploadProgress(0);
    }
  };

  const resolvedSelectedQuartierId =
    selectedAddressSuggestion?.source === 'quartier'
      ? (
          selectedAddressSuggestion.quartierId ||
          (selectedAddressSuggestion.id.startsWith('quartier-')
            ? selectedAddressSuggestion.id.slice('quartier-'.length)
            : '')
        )
      : '';

  return (
    <Container maxWidth={false} disableGutters className="addads-page">
      <Box className="addads-shell">
        <Typography variant="h4" component="h1" gutterBottom className="addads-title">
          {isEditMode ? t('addAds.editTitle') : t('addAds.createTitle')}
        </Typography>

        {isEditingAdLoading && (
          <div className="addads-inline-loader" role="status" aria-live="polite">
            <div className={loaderStyles.spinner} aria-hidden="true" />
            <p>{t('addAds.editLoading', { defaultValue: "Chargement de l'annonce..." })}</p>
          </div>
        )}

        {!isEditingAdLoading && isEditMode && !currentAd && !loading && (
          <div className="addads-load-error" role="alert">
            {t('addAds.editLoadError', { defaultValue: "Impossible de charger cette annonce pour le moment." })}
          </div>
        )}

        {!isEditingAdLoading && (!isEditMode || currentAd) && (
        <Box component="form" onSubmit={handleSubmit} className="addads-form">
          <Stack spacing={2} className="addads-stack">

            {/* 🔹 Titre (ancien champ, inchangé) */}
            <TextField
              label={t('addAds.titleLabel')}
              name="title"
              value={formData.title}
              onChange={(e) => handleChange(e)}
              inputProps={{ maxLength: TITLE_MAX_LENGTH }}
              fullWidth
              error={submitted && Boolean(getTitleValidationError(formData.title, t))}
              helperText={submitted ? getTitleValidationError(formData.title, t) : ""}
            />

            {/* 🔹 Description (ancien champ, inchangé) */}
            <div className="addads-description-field">
              <TextField
                label={t('addAds.descriptionLabel')}
                name="content"
                value={formData.content}
                onChange={handleChange}
                inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                multiline
                rows={4}
                fullWidth
                error={submitted && Boolean(getDescriptionValidationError(formData.content, t))}
                helperText={submitted ? getDescriptionValidationError(formData.content, t) : " "}
              />
              <div className="addads-description-meta">
                <span className="addads-description-helper-spacer" aria-hidden="true" />
                <span className="addads-description-counter">
                  {t('addAds.descriptionRemaining', {
                    count: Math.max(0, DESCRIPTION_MAX_LENGTH - formData.content.length),
                  })}
                </span>
              </div>
            </div>
            <div className={`addads-row addads-row--three addads-row--property-core ${formData.type === "appartement" ? "addads-row--property-core-apartment" : ""}`}>
              {/* 🔹 Nembre de chambre */}
              <TextField
                select
                label={t('addAds.roomNumberLabel')}
                name="room_nbr"
                value={formData.room_nbr}
                onChange={handleChange}
                fullWidth
                error={submitted && !formData.room_nbr}
                helperText={
                  submitted && !formData.room_nbr
                    ? t('addAds.roomNumberRequired')
                    : ""
                }
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
              </TextField>

              <TextField
                select
                label={t('addAds.typeLabel')}
                name="type"
                className="addads-field--type"
                value={formData.type}
                onChange={handleChange}
                fullWidth
                error={submitted && !formData.type}
                helperText={submitted && !formData.type ? t('addAds.typeRequired') : ""}
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {t(`adTypes.${option.key}`, { defaultValue: option.fr })}
                  </option>
                ))}
              </TextField>

              {formData.type === "appartement" ? (
                <TextField
                  select
                  label={t('addAds.floorCountLabel')}
                  name="nbr_floors"
                  className="addads-field--floors"
                  value={formData.nbr_floors}
                  onChange={handleChange}
                  fullWidth
                  error={submitted && Boolean(getFloorValidationError(formData.type, formData.nbr_floors, t))}
                  helperText={submitted ? getFloorValidationError(formData.type, formData.nbr_floors, t) : ""}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value=""></option>
                  {Array.from({ length: 20 }, (_, index) => index).map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </TextField>
              ) : null}

              {formData.type !== "appartement" && (
                <TextField
                  select
                  label={t('addAds.currencyLabel')}
                  name="currency"
                  className="addads-field--currency"
                  value={formData.currency}
                  onChange={handleChange}
                  fullWidth
                  error={submitted && !formData.currency}
                  helperText={submitted && !formData.currency ? t('addAds.currencyRequired') : ""}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value=""></option>
                  {Object.values(Currenci).map((cur) => (
                    <option key={cur} value={cur}>
                      {capitalizeFirstLetter(t(`currencies.${cur}`, { defaultValue: cur }))} ({cur})
                    </option>
                  ))}
                </TextField>
              )}
            </div>
            <div className="space"></div>

            <div className={`addads-row ${formData.type === "appartement" ? "addads-row--three" : "addads-row--two"}`}>
              {formData.type === "appartement" && (
                <TextField
                  select
                  label={t('addAds.currencyLabel')}
                  name="currency"
                  className="addads-field--currency"
                  value={formData.currency}
                  onChange={handleChange}
                  fullWidth
                  error={submitted && !formData.currency}
                  helperText={submitted && !formData.currency ? t('addAds.currencyRequired') : ""}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value=""></option>
                  {Object.values(Currenci).map((cur) => (
                    <option key={cur} value={cur}>
                      {capitalizeFirstLetter(t(`currencies.${cur}`, { defaultValue: cur }))} ({cur})
                    </option>
                  ))}
                </TextField>
              )}

              <TextField
                select
                label={t('addAds.bathroomCountLabel')}
                name="nbr_bathroom"
                value={formData.nbr_bathroom}
                onChange={handleChange}
                fullWidth
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                {[0, 1, 2, 3].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </TextField>

              <TextField
                select
                label={t('addAds.kitchenCountLabel')}
                name="nbr_kitchen"
                value={formData.nbr_kitchen}
                onChange={handleChange}
                fullWidth
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                {[0, 1, 2, 3].map((value) => (
                <option key={value} value={value}>{value}</option>
                ))}
              </TextField>
            </div>

            <div className="space"></div>

            <div className="addads-row addads-row--one">
              <AddressAutocompleteField
                label={t('addAds.addressLabel')}
                name="adress"
                value={formData.adress}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, adress: value }));
                  setPendingManualAddress(null);
                }}
                onSelectionChange={(isSelected) => {
                  setIsAddressSelectionValid(isSelected);
                  if (!isSelected) {
                    setSelectedAddressSuggestion(null);
                  }
                }}
                onSuggestionChange={(option) => {
                  setSelectedAddressSuggestion(option);
                  setPendingManualAddress(
                    option?.source === 'quartier' && option.address && option.wilayaId && option.communeId && !option.quartierId
                      ? {
                          address: option.address,
                          wilayaId: option.wilayaId,
                          communeId: option.communeId,
                        }
                      : null
                  );
                  setIsAddressSelectionValid(Boolean(option && option.source === 'quartier'));
                }}
                onEmptyActionClick={openAddAddressModal}
                extraOptions={memoryAddressOptions}
                emptyOptionLabel={t('addAds.addressNoResults')}
                emptyOptionClickable
                closeSuggestions={isAddAddressModalOpen}
                placeholder="Emplacement géographique du bien"
                required
                error={submitted && (!formData.adress.trim() || !isAddressSelectionValid)}
                helperText={
                  submitted && !formData.adress.trim()
                    ? t('addAds.addressRequired')
                    : submitted && !isAddressSelectionValid
                      ? t('addAds.addressSelectionRequired')
                      : ""
                }
              />
            </div>

            <div className="space"></div>

            <div className="addads-row addads-row--three">
              <TextField
                label={t('addAds.areaLabel')}
                type="number"
                name="area"
                value={formData.area}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 0, max: AREA_MAX_VALUE, inputMode: "numeric", pattern: "\\d{0,4}" }}
                error={submitted && (!formData.area || isNaN(Number(formData.area)))}
                helperText={
                  submitted && (!formData.area || isNaN(Number(formData.area)))
                    ? t('addAds.areaRequired')
                    : ""
                }
              />

              <TextField
                label={t('addAds.priceLabel')}
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                fullWidth
                inputProps={{
                  min: 0,
                  maxLength: getPriceMaxDigits(formData.currency),
                  inputMode: "numeric",
                  pattern: `\\d{0,${getPriceMaxDigits(formData.currency)}}`,
                }}
                error={submitted && Boolean(getPriceValidationError(formData.price, formData.currency, t))}
                helperText={
                  submitted ? getPriceValidationError(formData.price, formData.currency, t) : ""
                }
              />

              <TextField
                select
                label={t('addAds.minimumNightsLabel')}
                name="min_nights"
                value={formData.min_nights}
                onChange={handleChange}
                fullWidth
                SelectProps={{
                  native: true,
                }}
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </TextField>
            </div>

            <div className="addads-particularites">
              <label className="addads-label">{t('addAds.particularitiesLabel')}</label>
              <div className="addads-particularites-grid">
                {PARTICULARITY_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.key}
                    className="addads-particularites-item"
                    control={
                      <Checkbox
                        name="particularites"
                        value={option.key}
                        checked={formData.particularites.includes(option.key)}
                        onChange={handleChange}
                      />
                    }
                    label={t(`addAds.particularitiesOptions.${option.key}`, { defaultValue: option.fr })}
                  />
                ))}
              </div>
            </div>

            <div className="space"></div>

            <FormControlLabel
              control={
                <Checkbox
                  name="desactiver_commentaire"
                  checked={formData.desactiver_commentaire}
                  onChange={handleChange}
                />
              }
              label={t("addAds.disableCommentsLabel", { defaultValue: "Désactiver les commentaires pour cette annonce" })}
            />

            <div className="space"></div>

            <label htmlFor="photos" className="addads-label">{t('addAds.photosLabel')}</label>
            <input
              id="photos"
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotosChange}
              disabled={photoPreviews.length >= MAX_PHOTOS || isCompressingPhotos}
              className="addads-file-input"
            />

            {isCompressingPhotos && (
              <Typography variant="body2">
                {t('addAds.compressingPhotos', { defaultValue: 'Compression des images...' })}
              </Typography>
            )}

            {photoPreviews.length > 0 && (
              <>
                <FormControlLabel
                  className="photo-priority-toggle"
                  labelPlacement="start"
                  control={
                    <Checkbox
                      checked={isPriorityPhotoSelectionEnabled}
                      onChange={(event) => {
                        const enabled = event.target.checked;
                        setIsPriorityPhotoSelectionEnabled(enabled);
                        if (!enabled) {
                          setSelectedPriorityPhotoKey(null);
                        }
                      }}
                    />
                  }
                  label={t('addAds.enablePriorityPhotoSelection', {
                    defaultValue: 'Activer la sélection de la photo principale',
                  })}
                />

                <div className="photo-preview-grid">
                  {photoPreviews.map((preview) => (
                    <figure key={preview.key} className="photo-preview-card">
                      <button
                        type="button"
                        className="photo-remove-btn"
                        onClick={() => handleRemoveSelectedPhoto(preview.key)}
                        aria-label={t('addAds.removePhoto', { name: preview.name })}
                      >
                        x
                      </button>
                      <img src={preview.url} alt={preview.name} className="photo-preview-image" />
                      {isPriorityPhotoSelectionEnabled && (
                        <Checkbox
                          className="photo-priority-checkbox"
                          checked={selectedPriorityPhotoKey === preview.key}
                          onChange={(event) => handlePriorityPhotoChange(preview.key, event.target.checked)}
                          inputProps={{
                            "aria-label": t('addAds.priorityPhoto', {
                              name: preview.name,
                              defaultValue: `Photo prioritaire ${preview.name}`,
                            }),
                          }}
                        />
                      )}
                    </figure>
                  ))}
                </div>
              </>
            )}

            {(uploadingPhotos || uploadProgress > 0) && (
              <Box>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption">{uploadProgress}%</Typography>
              </Box>
            )}

            {uploadError && <p className="error-text">{uploadError}</p>}
            <div className="space"></div>

            {!user && <div className="addads-user-block">
              <div className="addads-row addads-row--two">
                {/* 🔹 Nom */}
                <TextField
                  label={t('addAds.lastName')}
                  value={formData.lastName}
                  name="lastName"
                  onChange={handleChange}
                  fullWidth
                  error={submitted && !formData.lastName}
                  helperText={submitted && !formData.lastName ? t('addAds.lastNameRequired') : ""}
                />

                {/* 🔹 Prénom */}
                <TextField
                  label={t('addAds.firstName')}
                  value={formData.firstName}
                  name="firstName"
                  onChange={handleChange}
                  fullWidth
                  error={submitted && !formData.firstName}
                  helperText={submitted && !formData.firstName ? t('addAds.firstNameRequired') : ""}
                />
              </div>
              <div className="space"></div>

              <div className="addads-row addads-row--one">
                <TextField
                  label={t('addAds.email')}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  error={submitted && !formData.email}
                  helperText={submitted && !formData.email ? t('addAds.emailRequired') : ""}
                />

              </div>
              <div className="space"></div>

              <div className="addads-row addads-row--two">
                {/* 🔹 Mot de passe */}
                <TextField
                  label={t('addAds.password')}
                  type="password"
                  value={formData.password}
                  name="password"
                  onChange={handleChange}
                  fullWidth
                  error={submitted && !formData.password}
                  helperText={
                    submitted && !formData.password ? t('addAds.passwordRequired') : ""
                  }
                />

                {/* 🔹 Confirmation mot de passe */}
                <TextField
                  label={t('addAds.confirmPassword')}
                  type="password"
                  value={formData.confirmPassword}
                  name="confirmPassword"
                  onChange={handleChange}
                  fullWidth
                  error={
                    submitted &&
                    (!!formData.confirmPassword === false || formData.password !== formData.confirmPassword)
                  }
                  helperText={
                    submitted && !formData.confirmPassword
                      ? t('addAds.confirmPasswordRequired')
                      : submitted && formData.password !== formData.confirmPassword
                        ? t('addAds.confirmPasswordMismatch')
                        : ""
                  }
                />
              </div>
              <div className="space"></div>
              <label htmlFor="phone" className="addads-label">{t('addAds.phone')}</label>
              <div style={{ marginTop: 8 }}></div>
              <div style={{ width: "100%" }}>
                <PhoneInput onChange={handleChange} value={formData.phone} name="phone" />
              </div>
              {errorPhone && <p className="error-text">{errorPhone}</p>}

            </div>}
            <>
              <SimpleCaptcha
                idPrefix="addads"
                challenge={captchaChallenge}
                value={captchaInput}
                onChange={(value) => {
                  setCaptchaInput(value);
                  setCaptchaError("");
                }}
                onRefresh={() => {
                  void refreshCaptchaChallenge();
                }}
                label={t('captcha.label')}
                placeholder={t('captcha.placeholder')}
                refreshLabel={t('captcha.refresh')}
                error={captchaError}
              />
              <div className="space"></div>
            </>
            <Button type="submit" variant="contained" className="addads-submit-btn" disabled={submittingAd || isCompressingPhotos}>
              {isCompressingPhotos
                ? t('addAds.compressingPhotos', { defaultValue: 'Compression des images...' })
                : uploadingPhotos
                ? t('addAds.uploadingPhotos')
                : loading
                  ? t('addAds.saving')
                  : isEditMode
                    ? "Modifier"
                    : t('addAds.create')}
            </Button>
            <div className="space"></div>
          </Stack>
        </Box>
        )}
      </Box>
      {submittingAd && (
        <div className={`${loaderStyles.overlay} ${loaderStyles.active}`} role="status" aria-live="polite">
          <div className={loaderStyles.spinner} aria-hidden="true" />
          <p className={loaderStyles.label}>{t('loader.pageLoading')}</p>
        </div>
      )}
      {createdAdNotice && (
        <div
          className="addads-confirm-overlay"
          onClick={() => {
            setCreatedAdNotice(null);
            navigate("/");
          }}
        >
          <div className="addads-confirm-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <h3>{t('addAds.pendingValidationTitle')}</h3>
            <p>{t('addAds.pendingValidationMessage')}</p>
            <div className="addads-confirm-actions">
              <button
                type="button"
                className="addads-confirm-close"
                onClick={() => {
                  setCreatedAdNotice(null);
                  navigate("/");
                }}
              >
                {t('addAds.pendingValidationClose')}
              </button>
            </div>
          </div>
        </div>
      )}
      {isAddAddressModalOpen && (
        <div
          className="addads-confirm-overlay"
        >
          <div
            className="addads-address-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>{t('addAds.addressModalTitle')}</h3>
            <p>{t('addAds.addressModalSubtitle')}</p>
            <div className="space"></div>
            <div className="addads-address-modal-row">
              <TextField
                label={t('addAds.addressModalAddressLabel')}
                name="address"
                value={manualAddressForm.address}
                onChange={handleManualAddressFieldChange}
                fullWidth
                helperText={t('addAds.addressModalAddressHint')}
                FormHelperTextProps={{
                  className: 'addads-address-hint',
                }}
              />
              <TextField
                select
                label={t('addAds.addressModalWilayaLabel')}
                name="wilayaId"
                value={manualAddressForm.wilayaId}
                onChange={handleManualAddressFieldChange}
                fullWidth
                disabled={isWilayaOptionsLoading}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ MenuProps: ADDRESS_MODAL_MENU_PROPS }}
              >
                <MenuItem value="">
                  {isWilayaOptionsLoading
                    ? t('addAds.addressModalLoadingWilayas')
                    : t('addAds.addressModalSelectWilaya')}
                </MenuItem>
                {wilayaOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('addAds.addressModalCommuneLabel')}
                name="communeId"
                value={manualAddressForm.communeId}
                onChange={handleManualAddressFieldChange}
                fullWidth
                disabled={!manualAddressForm.wilayaId || isCommuneOptionsLoading}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ MenuProps: ADDRESS_MODAL_MENU_PROPS }}
              >
                <MenuItem value="">
                  {isCommuneOptionsLoading
                    ? t('addAds.addressModalLoadingCommunes')
                    : t('addAds.addressModalSelectCommune')}
                </MenuItem>
                {communeOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div className="addads-address-map-picker">
              <button
                type="button"
                className="addads-confirm-view addads-address-map-button"
                onClick={() => {
                  void handleOpenManualAddressMap();
                }}
                disabled={isCreatingManualAddress || isOpeningManualAddressMap || isReverseGeocodingManualAddress || !canOpenManualAddressMap}
              >
                {isOpeningManualAddressMap || isReverseGeocodingManualAddress
                  ? t('addAds.addressModalMapLoading', { defaultValue: "Generation de l'adresse..." })
                  : "Ajouter l'adresse depuis la carte"}
              </button>
              <p className="addads-address-map-helper">
                Pour ajouter l'adresse depuis la carte en deplaçant le pointeur (mappeur) il faut renseigner la ville et la commune
              </p>
            </div>
            {manualAddressError ? <p className="error-text">{manualAddressError}</p> : null}
            {manualAddressGeocodeResult ? (
              <>
                <div
                  ref={manualAddressMapContainerRef}
                  className="addads-address-geocode-map"
                  aria-label={t('addAds.addressModalGeocodeMapLabel')}
                />
                <p className="addads-address-map-helper">
                  Double-cliquez sur la carte pour replacer le pointeur dans une autre zone.
                </p>
                {manualAddressGeocodeError ? (
                  <div className="addads-address-geocode-error" role="alert">
                    {manualAddressGeocodeError}
                  </div>
                ) : null}
              </>
            ) : manualAddressGeocodeError ? (
              <div className="addads-address-geocode-error" role="alert">
                {manualAddressGeocodeError}
              </div>
            ) : null}
            <div className="addads-confirm-actions">
              <button
                type="button"
                className="addads-confirm-view addads-address-geocode-button"
                onClick={() => {
                  void handleTestManualAddressGeocode();
                }}
                disabled={isCreatingManualAddress || isTestingManualAddressGeocode || !isManualAddressFormValid}
              >
                {isTestingManualAddressGeocode ? t('addAds.addressModalGeocodeTesting') : t('addAds.addressModalGeocodeTest')}
              </button>
              <button
                type="button"
                className="addads-confirm-view"
                onClick={() => {
                  void handleCreateManualAddress();
                }}
                disabled={isCreatingManualAddress || !isManualAddressFormValid}
              >
                {isCreatingManualAddress ? t('addAds.addressModalSubmitting') : t('addAds.addressModalSubmit')}
              </button>
              <button
                type="button"
                className="addads-confirm-close"
                onClick={closeAddAddressModal}
                disabled={isCreatingManualAddress}
              >
                {t('addAds.addressModalCancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default AddAdPage;
